export type RynReplaySessionEndReason = 'idle' | 'maxDuration';

export interface IRynReplaySessionLifecycleConfig {
  /** End session after this many ms without any captured envelope. `0` disables. */
  idleTimeoutMs: number;
  /** End session after this many ms from session start (wall clock). `0` disables. */
  maxSessionDurationMs: number;
}

export interface IRynReplaySessionLifecycleState {
  sessionStartedMs: number;
  lastActivityMs: number;
}

export interface IRynReplaySessionLifecycleInput {
  nowMs: number;
  state: IRynReplaySessionLifecycleState;
  config: IRynReplaySessionLifecycleConfig;
}

/** Recommended idle gap when an app opts into walk-away segmentation (30 minutes). */
export const RYN_REPLAY_DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;

/** Recommended wall-clock cap when an app opts in (8 hours). Not a SmartLook-style 1 h stop. */
export const RYN_REPLAY_DEFAULT_MAX_SESSION_DURATION_MS = 8 * 60 * 60 * 1000;

export const defaultRynReplaySessionLifecycleConfig =
  (): IRynReplaySessionLifecycleConfig => ({
    idleTimeoutMs: RYN_REPLAY_DEFAULT_IDLE_TIMEOUT_MS,
    maxSessionDurationMs: RYN_REPLAY_DEFAULT_MAX_SESSION_DURATION_MS,
  });

export const resolveRynReplaySessionLifecycleConfig = (options?: {
  idleTimeoutMs?: number;
  maxSessionDurationMs?: number;
}): IRynReplaySessionLifecycleConfig => {
  const defaults = defaultRynReplaySessionLifecycleConfig();
  return {
    idleTimeoutMs:
      typeof options?.idleTimeoutMs === 'number'
        ? options.idleTimeoutMs
        : defaults.idleTimeoutMs,
    maxSessionDurationMs:
      typeof options?.maxSessionDurationMs === 'number'
        ? options.maxSessionDurationMs
        : defaults.maxSessionDurationMs,
  };
};

/**
 * Returns why the recorder should rotate to a new session id, or null while the
 * current segment should continue.
 */
export const evaluateRynReplaySessionRotation = (
  input: IRynReplaySessionLifecycleInput
): RynReplaySessionEndReason | null => {
  const { nowMs, state, config } = input;

  if (
    config.idleTimeoutMs > 0 &&
    nowMs - state.lastActivityMs >= config.idleTimeoutMs
  ) {
    return 'idle';
  }

  if (
    config.maxSessionDurationMs > 0 &&
    nowMs - state.sessionStartedMs >= config.maxSessionDurationMs
  ) {
    return 'maxDuration';
  }

  return null;
};
